// ============================================================================
// WebAuthnEnrollFlow
// Owner: Claude 3 (Frontend & UX). Updated Phase 5.5 closure: real base64url
// codec via `auth/webauthn.ts` replaces the prior stub that passed the server
// blob straight through.
// Purpose: WebAuthn enroll using navigator.credentials.create().
// URS rows: AUTH-003..006, SEC-021 (mandatory for QA tier).
// ============================================================================
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebAuthnOptions, useWebAuthnRegister } from '../../api/twoFactorHooks';
import {
  decodeRegistrationOptions,
  encodeRegistrationResponse,
  isWebAuthnSupported,
  type ServerPublicKeyCredentialCreationOptions,
} from '../../auth/webauthn';

export function WebAuthnEnrollFlow({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const opts = useWebAuthnOptions();
  const register = useWebAuthnRegister();
  const [error, setError] = useState<string | null>(null);

  const supported = isWebAuthnSupported();

  const onEnroll = async () => {
    setError(null);
    if (!supported) {
      setError(t('twofa.webauthn.platform_prompt'));
      return;
    }
    try {
      // Server sends base64url-encoded challenge + user.id + excludeCredentials[].id.
      // Per the WebAuthn spec, every one of those MUST be decoded to an
      // ArrayBuffer before being passed to navigator.credentials.create().
      const serverOpts = (await opts.mutateAsync()) as unknown as ServerPublicKeyCredentialCreationOptions;
      const clientOpts = decodeRegistrationOptions(serverOpts);
      const credential = (await navigator.credentials.create({ publicKey: clientOpts })) as PublicKeyCredential | null;
      if (!credential) {
        setError(t('twofa.webauthn.cancelled', { defaultValue: 'WebAuthn enrollment was cancelled.' }));
        return;
      }
      // Re-encode the response for the wire — the server expects base64url
      // for every ArrayBuffer field per the spec.
      const wire = encodeRegistrationResponse(credential);
      await register.mutateAsync({ attestation: wire });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      <h2>{t('twofa.webauthn.card_title')}</h2>
      <p>{t('twofa.webauthn.platform_prompt')}</p>
      <p>{t('twofa.webauthn.cross_platform_prompt')}</p>
      {!supported && <p role="alert">{t('twofa.webauthn.unsupported')}</p>}
      <button onClick={onEnroll} disabled={!supported || opts.isPending || register.isPending}>
        {t('twofa.common.set_up')}
      </button>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
